import React, { useEffect, useRef, useState } from 'react'

type Line = { ts: string; text: string }

export const SyncConsole: React.FC = () => {
	const [open, setOpen] = useState(false)
	const [lines, setLines] = useState<Line[]>([])
	const endRef = useRef<HTMLDivElement>(null)

	useEffect(() => {
		function push(text: string) {
			setLines(prev => [...prev, { ts: new Date().toLocaleTimeString(), text }].slice(-200))
			setOpen(true)
		}
		const handler = (e: Event) => {
			const d = (e as CustomEvent).detail || {}
			const s = d.stage as string | undefined
			const trigger = d.trigger ? ` [${d.trigger}]` : ''
			if (!s) return
			if (s === 'sync:phase') {
				const phase = d.phase || 'unknown'
				const msg = d.message ? ` · ${d.message}` : ''
				push(`[sync]${trigger} phase=${phase}${msg}`)
			} else if (s === 'sync:auto-check:start') {
				push(`[sync]${trigger} auto-check start`)
			} else if (s === 'sync:auto-check:done') {
				push(`[sync]${trigger} auto-check done`)
			} else if (s === 'sync:auto-check:skipped') {
				push(`[sync]${trigger} auto-check omitido (${d.reason || 'policy'})`)
			} else if (s === 'sync:auto-pull:executing') {
				push(`[sync]${trigger} auto-pull ejecutado`)
			} else if (s === 'sync:auto-pull:done') {
				push(`[sync]${trigger} auto-pull done`)
			} else if (s === 'sync:auto-pull:skipped') {
				push(`[sync]${trigger} auto-pull omitido (${d.reason || 'policy'})${d.message ? ` · ${d.message}` : ''}`)
			} else if (s === 'sync:strategy:selected') {
				const strategy = d.strategy_selected || 'unknown'
				const blocked = d.strict_blocked_unverified ? 'yes' : 'no'
				push(`[sync]${trigger} strategy=${strategy} strict_blocked_unverified=${blocked}`)
			} else if (s === 'pull:phase:start') {
				push(`[pull]${trigger} ${d.phase || 'unknown'} start`)
			} else if (s === 'pull:phase:ok') {
				const parts: string[] = []
				if (typeof d.count === 'number') parts.push(`rows ${d.count}`)
				if (typeof d.durationMs === 'number') parts.push(`${d.durationMs}ms`)
				push(`[pull]${trigger} ${d.phase || 'unknown'} ok${parts.length ? ` (${parts.join(' · ')})` : ''}`)
			} else if (s === 'pull:phase:error') {
				const parts: string[] = []
				if (typeof d.durationMs === 'number') parts.push(`${d.durationMs}ms`)
				push(`[pull]${trigger} ${d.phase || 'unknown'} ERROR${parts.length ? ` (${parts.join(' · ')})` : ''}: ${d.error || 'desconocido'}`)
			} else if (s === 'push:phase:warn') {
				const parts: string[] = []
				if (typeof d.count === 'number') parts.push(`rows ${d.count}`)
				if (d.continued) parts.push('continued')
				push(`[push]${trigger} ${d.phase || 'unknown'} WARN${parts.length ? ` (${parts.join(' · ')})` : ''}: ${d.error || 'warning'}`)
			} else if (s === 'sync:watermark:repaired') {
				push(`[sync]${trigger} watermark reparado (${d.table || 'unknown'}) razón=${d.reason || 'unknown'}`)
			} else if (s === 'sync:safe-opened') {
				push(`[sync]${trigger} safe_sync abierta para revisión manual`)
			} else if (s === 'sync:warning') {
				push(`[sync]${trigger} warning: ${d.message || 'completed with warnings'}`)
			} else if (s.startsWith('pull:')) {
				const suffix = typeof d.count === 'number'
					? ` (${d.count})`
					: d.batch
						? ` (+${d.batch}, total ${d.total})`
						: ''
				push(`[pull]${trigger} ${s.replace('pull:', '')}${suffix}`)
			} else if (s.startsWith('sync:')) push(`[sync]${trigger} ${s.replace('sync:', '')}`)
			else if (s.startsWith('push:')) push(`[push]${trigger} ${s.replace('push:', '')}`)
			if (s.endsWith(':error') || s === 'sync:error') {
				push(`error: ${d.error || 'desconocido'}`)
			}
		}
		window.addEventListener('wrlens:sync-progress', handler)
		return () => window.removeEventListener('wrlens:sync-progress', handler)
	}, [])

	useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }) }, [lines])

	if (!open) return null

	return (
		<div className="fixed bottom-4 right-4 w-[420px] max-h-[40vh] bg-black/80 border border-white/20 rounded-lg shadow-2xl backdrop-blur p-3 text-xs z-50 flex flex-col">
			<div className="flex items-center justify-between mb-2">
				<div className="font-semibold text-[#F8F6EF]">Sincronización</div>
				<button className="px-2 py-1 text-[11px] rounded bg-gray-700 hover:bg-gray-600" onClick={() => setOpen(false)}>Ocultar</button>
			</div>
			<div className="flex-1 overflow-auto space-y-1">
				{lines.map((l, i) => (
					<div key={i} className="text-[#ebd4b5] whitespace-pre-wrap"><span className="opacity-60">[{l.ts}]</span> {l.text}</div>
				))}
				<div ref={endRef} />
			</div>
		</div>
	)
}

export default SyncConsole
